import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OppdragsbekreftelseSkjema.css';

/**
 * Oppdragsbekreftelse Skjema
 *
 * Henter data fra CRM, ber om manglende felter, genererer oppdragsbekreftelse
 */
export default function OppdragsbekreftelseSkjema({ sakId, saksnummer, onLukk }) {
  // Loading states
  const [lasterData, setLasterData] = useState(true);
  const [genererer, setGenererer] = useState(false);
  const [feil, setFeil] = useState(null);

  // Saksdata fra CRM
  const [saksdata, setSaksdata] = useState(null);

  // Skjemafelter (kan redigeres)
  const [klientNavn, setKlientNavn] = useState('');
  const [klientAdresse, setKlientAdresse] = useState('');
  const [klientPostnr, setKlientPostnr] = useState('');
  const [klientPoststed, setKlientPoststed] = useState('');
  const [oppdragetGjelder, setOppdragetGjelder] = useState('');
  const [timeprisEksMva, setTimeprisEksMva] = useState('');
  const [advokatNavn, setAdvokatNavn] = useState('');

  // Generert dokument
  const [generertDokument, setGenerertDokument] = useState(null);

  /**
   * Hent saksdata fra CRM ved oppstart
   */
  useEffect(() => {
    hentSaksdata();
  }, [sakId]);

  const hentSaksdata = async () => {
    setLasterData(true);
    setFeil(null);

    try {
      // Backend henter automatisk: klient, advokat, timepris, etc
      const response = await axios.get(`/api/oppdragsbekreftelse/${sakId}/data`);

      if (response.data.success) {
        const data = response.data.data;
        setSaksdata(data);

        // Pre-fyll skjema med data fra CRM
        setKlientNavn(data.klient_navn || '');
        setKlientAdresse(data.klient_adresse || '');
        setKlientPostnr(data.klient_postnr || '');
        setKlientPoststed(data.klient_poststed || '');
        setOppdragetGjelder(data.sakstittel || data.saksbeskrivelse || '');
        setTimeprisEksMva(data.timepris_eks_mva || 1500);
        setAdvokatNavn(data.advokat_navn || '');
      } else {
        setFeil(response.data.error || 'Kunne ikke hente saksdata');
      }
    } catch (error) {
      console.error('Feil ved henting av saksdata:', error);
      setFeil(error.response?.data?.error || 'Kunne ikke hente saksdata');
    } finally {
      setLasterData(false);
    }
  };

  /**
   * Generer oppdragsbekreftelse
   */
  const genererOppdragsbekreftelse = async () => {
    // Validering
    if (!klientNavn.trim()) {
      setFeil('Klientnavn er påkrevd');
      return;
    }
    if (!oppdragetGjelder.trim()) {
      setFeil('"Oppdraget gjelder" er påkrevd');
      return;
    }
    if (!timeprisEksMva || timeprisEksMva <= 0) {
      setFeil('Timepris må være større enn 0');
      return;
    }
    if (!advokatNavn.trim()) {
      setFeil('Advokatnavn er påkrevd');
      return;
    }

    setGenererer(true);
    setFeil(null);

    try {
      const response = await axios.post('/api/oppdragsbekreftelse/generer', {
        sak_id: sakId,
        klient_navn: klientNavn,
        klient_adresse: klientAdresse,
        klient_postnr: klientPostnr,
        klient_poststed: klientPoststed,
        oppdraget_gjelder: oppdragetGjelder,
        timepris_eks_mva: parseInt(timeprisEksMva),
        advokat_navn: advokatNavn
      });

      if (response.data.success) {
        setGenerertDokument(response.data.data);
      } else {
        setFeil(response.data.error || 'Kunne ikke generere oppdragsbekreftelse');
      }
    } catch (error) {
      console.error('Feil ved generering:', error);
      setFeil(error.response?.data?.error || 'Kunne ikke generere oppdragsbekreftelse');
    } finally {
      setGenererer(false);
    }
  };

  /**
   * Beregn timepris inkl. mva
   */
  const timeprisInklMva = timeprisEksMva ? Math.round(parseInt(timeprisEksMva) * 1.25) : 0;

  /**
   * Last ned HTML
   */
  const lastNedHTML = () => {
    if (!generertDokument || !generertDokument.html) return;

    const blob = new Blob([generertDokument.html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oppdragsbekreftelse_${saksnummer}_${klientNavn.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Last ned som PDF (direkte via nettleser)
   */
  const lastNedPDF = () => {
    if (!generertDokument || !generertDokument.html) return;

    // Åpne HTML i nytt vindu
    const vindu = window.open('', '_blank');
    vindu.document.write(generertDokument.html);
    vindu.document.close();

    // Vent til siden er lastet, så trigger utskrift til PDF
    vindu.onload = () => {
      setTimeout(() => {
        vindu.print();
      }, 250);
    };
  };

  /**
   * Åpne forhåndsvisning
   */
  const aapneForhaandsvisning = () => {
    if (!generertDokument || !generertDokument.html) return;

    const vindu = window.open('', '_blank');
    vindu.document.write(generertDokument.html);
    vindu.document.close();
  };

  /**
   * Skriv ut
   */
  const skrivUt = () => {
    if (!generertDokument || !generertDokument.html) return;

    const vindu = window.open('', '_blank');
    vindu.document.write(generertDokument.html);
    vindu.document.close();
    vindu.onload = () => vindu.print();
  };

  // Laster data
  if (lasterData) {
    return (
      <div className="oppdragsbekreftelse-laster">
        <div className="spinner"></div>
        <p>Henter saksdata...</p>
      </div>
    );
  }

  // Vis generert dokument
  if (generertDokument) {
    return (
      <div className="oppdragsbekreftelse-resultat">
        <div className="resultat-header">
          <h2>✅ Oppdragsbekreftelse generert!</h2>
          {onLukk && <button className="lukk-knapp" onClick={onLukk}>✕</button>}
        </div>

        <div className="resultat-info">
          <p><strong>Klient:</strong> {klientNavn}</p>
          <p><strong>Timepris:</strong> {timeprisEksMva.toLocaleString('no-NO')} kr eks. mva = {timeprisInklMva.toLocaleString('no-NO')} kr inkl. mva</p>
          <p><strong>Generert:</strong> {new Date().toLocaleString('no-NO')}</p>
        </div>

        <div className="resultat-handlinger">
          <button className="handling-knapp primær" onClick={lastNedPDF}>
            📄 Last ned PDF
          </button>
          <button className="handling-knapp" onClick={lastNedHTML}>
            💾 Last ned HTML
          </button>
          <button className="handling-knapp" onClick={aapneForhaandsvisning}>
            👁️ Forhåndsvis
          </button>
        </div>

        <div className="resultat-instruksjoner">
          <h3>📌 Neste steg:</h3>
          <ol>
            <li><strong>Klikk "📄 Last ned PDF"</strong> - Nettleseren åpner utskriftsdialog</li>
            <li>Velg "Lagre som PDF" / "Microsoft Print to PDF"</li>
            <li>Velg filnavn og lagre</li>
            <li>Gå til <a href="https://signering.posten.no" target="_blank" rel="noopener">signering.posten.no</a></li>
            <li>Last opp PDF og send til klient for BankID-signering</li>
            <li>Når signert: Last ned og lagre i saksmappen</li>
          </ol>
          <p style={{fontSize: '0.9em', color: '#666', marginTop: '10px'}}>
            💡 Tips: Du kan også laste ned som HTML og åpne i Word hvis du vil redigere først.
          </p>
        </div>

        <button className="ny-knapp" onClick={() => setGenerertDokument(null)}>
          🔄 Generer ny
        </button>
      </div>
    );
  }

  // Vis skjema
  return (
    <div className="oppdragsbekreftelse-skjema">
      <div className="skjema-header">
        <h2>📄 Opprett oppdragsbekreftelse</h2>
        {onLukk && <button className="lukk-knapp" onClick={onLukk}>✕</button>}
      </div>

      {feil && (
        <div className="feilmelding">
          <strong>⚠️ Feil:</strong> {feil}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); genererOppdragsbekreftelse(); }}>
        <div className="skjema-seksjon">
          <h3>Klientopplysninger</h3>

          <div className="felt">
            <label htmlFor="klientnavn">
              Klientnavn <span className="pakrevd">*</span>
            </label>
            <input
              id="klientnavn"
              type="text"
              value={klientNavn}
              onChange={(e) => setKlientNavn(e.target.value)}
              placeholder="Kari Nordmann"
              required
            />
          </div>

          <div className="felt">
            <label htmlFor="adresse">Adresse</label>
            <input
              id="adresse"
              type="text"
              value={klientAdresse}
              onChange={(e) => setKlientAdresse(e.target.value)}
              placeholder="Storgata 1"
            />
          </div>

          <div className="felt-rad">
            <div className="felt halv">
              <label htmlFor="postnr">Postnummer</label>
              <input
                id="postnr"
                type="text"
                value={klientPostnr}
                onChange={(e) => setKlientPostnr(e.target.value)}
                placeholder="0123"
              />
            </div>
            <div className="felt halv">
              <label htmlFor="poststed">Poststed</label>
              <input
                id="poststed"
                type="text"
                value={klientPoststed}
                onChange={(e) => setKlientPoststed(e.target.value)}
                placeholder="Oslo"
              />
            </div>
          </div>
        </div>

        <div className="skjema-seksjon">
          <h3>Oppdragsdetaljer</h3>

          <div className="felt">
            <label htmlFor="oppdraget">
              Oppdraget gjelder <span className="pakrevd">*</span>
            </label>
            <textarea
              id="oppdraget"
              value={oppdragetGjelder}
              onChange={(e) => setOppdragetGjelder(e.target.value)}
              placeholder="F.eks. 'Hjelp i forbindelse med boligkjøpstvist vedrørende...'"
              rows="3"
              required
            />
            <small className="hjelpetekst">
              Beskriv kort hva oppdraget gjelder (vises i oppdragsbekreftelsen)
            </small>
          </div>
        </div>

        <div className="skjema-seksjon">
          <h3>Timepris</h3>

          <div className="felt">
            <label htmlFor="timepris">
              Timepris eks. mva <span className="pakrevd">*</span>
            </label>
            <div className="input-med-enhet">
              <input
                id="timepris"
                type="number"
                value={timeprisEksMva}
                onChange={(e) => setTimeprisEksMva(e.target.value)}
                min="0"
                step="50"
                required
              />
              <span className="enhet">kr</span>
            </div>
            {timeprisEksMva > 0 && (
              <small className="hjelpetekst">
                = {timeprisInklMva.toLocaleString('no-NO')} kr inkl. mva (25%)
              </small>
            )}
          </div>
        </div>

        <div className="skjema-seksjon">
          <h3>Advokat</h3>

          <div className="felt">
            <label htmlFor="advokat">
              Ansvarlig advokat <span className="pakrevd">*</span>
            </label>
            <input
              id="advokat"
              type="text"
              value={advokatNavn}
              onChange={(e) => setAdvokatNavn(e.target.value)}
              placeholder="Stig Berthelsen Wagner"
              required
            />
          </div>
        </div>

        <div className="skjema-handlinger">
          <button
            type="submit"
            className="generer-knapp"
            disabled={genererer}
          >
            {genererer ? '⏳ Genererer...' : '🎯 Generer oppdragsbekreftelse'}
          </button>

          {onLukk && (
            <button type="button" className="avbryt-knapp" onClick={onLukk}>
              Avbryt
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
